import { useState } from "react";
import { Loader2, PowerOff } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  DialogHeader,
  DialogTitle,
} from "@sportscanner/ui";
import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from "@/components/responsive-dialog";
import { apiDelete, getApiErrorCode } from "@/lib/api";
import { dateStrToEntry } from "./helpers";

export interface ClosureDetail {
  id: string;
  date: string;
  reason: string;
  seriesId: string | null;
  createdByUserName: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clubId: string;
  closure: ClosureDetail | null;
  onRemoved?: () => void;
}

export default function ClosureRemoveDialog({
  open,
  onOpenChange,
  clubId,
  closure,
  onRemoved,
}: Props) {
  const { t } = useTranslation();
  const [confirmOne, setConfirmOne] = useState(false);
  const [confirmSeries, setConfirmSeries] = useState(false);

  const removeOne = useMutation({
    mutationFn: (id: string) =>
      apiDelete(`/api/admin/clubs/${clubId}/closures/${id}`),
    onSuccess: () => {
      toast.success(t("closures.remove.removedOneToast"));
      setConfirmOne(false);
      onOpenChange(false);
      onRemoved?.();
    },
    onError: (err: unknown) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  const removeSeries = useMutation({
    mutationFn: (seriesId: string) =>
      apiDelete<{ ok: true; deleted: number; fromDate: string }>(
        `/api/admin/clubs/${clubId}/closures/series/${seriesId}`,
      ),
    onSuccess: (data) => {
      toast.success(
        t("closures.remove.removedSeriesToast", { count: data.deleted }),
      );
      setConfirmSeries(false);
      onOpenChange(false);
      onRemoved?.();
    },
    onError: (err: unknown) => {
      toast.error(t(`errors.api.${getApiErrorCode(err)}`));
    },
  });

  const dateLabel = closure
    ? (() => {
        const e = dateStrToEntry(closure.date);
        return `${t(`days.long.${e.dayKey}`)}, ${e.date}. ${t(`months.${e.monthKey}`)}`;
      })()
    : "";

  return (
    <>
      <ResponsiveDialog open={open} onOpenChange={onOpenChange}>
        <ResponsiveDialogContent className="md:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PowerOff className="w-4 h-4 text-red-500" />
              {t("closures.remove.title")}
            </DialogTitle>
          </DialogHeader>

          {closure && (
            <div className="space-y-3">
              <div className="rounded-lg bg-red-50 border border-red-200 p-3 space-y-1.5 text-sm">
                <div className="font-medium text-neutral-800">{dateLabel}</div>
                <div className="text-neutral-600 italic">
                  "{closure.reason}"
                </div>
                {closure.createdByUserName && (
                  <div className="text-xs text-neutral-400">
                    {t("closures.remove.createdBy", {
                      name: closure.createdByUserName,
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2 pt-1">
                <Button
                  onClick={() => setConfirmOne(true)}
                  variant="outline"
                  className="w-full rounded-full text-red-600 border-red-200 hover:bg-red-50"
                >
                  {t("closures.remove.removeOne")}
                </Button>
                {closure.seriesId && (
                  <Button
                    onClick={() => setConfirmSeries(true)}
                    variant="outline"
                    className="w-full rounded-full text-red-600 border-red-200 hover:bg-red-50"
                  >
                    {t("closures.remove.removeSeries")}
                  </Button>
                )}
                <Button
                  onClick={() => onOpenChange(false)}
                  variant="outline"
                  className="w-full rounded-full"
                >
                  {t("closures.remove.close")}
                </Button>
              </div>
            </div>
          )}
        </ResponsiveDialogContent>
      </ResponsiveDialog>

      <AlertDialog open={confirmOne} onOpenChange={setConfirmOne}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("closures.remove.confirmOneTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("closures.remove.confirmOneDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeOne.isPending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={removeOne.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (closure) removeOne.mutate(closure.id);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {removeOne.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("closures.remove.removeOne")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmSeries} onOpenChange={setConfirmSeries}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("closures.remove.confirmSeriesTitle")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t("closures.remove.confirmSeriesDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeSeries.isPending}>
              {t("common.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={removeSeries.isPending}
              onClick={(e) => {
                e.preventDefault();
                if (closure?.seriesId) removeSeries.mutate(closure.seriesId);
              }}
              className="bg-red-600 hover:bg-red-700"
            >
              {removeSeries.isPending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                t("closures.remove.removeSeries")
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
